import type { SessionSummary, SetupCheckResult, SetupCompleteResult, SignIn, SignInResult } from "../../contracts";

// The seam between the browser and the access routes (DSH-015 phase 2): the
// sign-in dialog posts here and the header asks whether this browser holds a
// session. The session cookie is httpOnly; scripts never see it.

const SIGN_IN = "/api/access/sign-in";
const SESSION = "/api/access/session";

export const signIn: SignIn = async (request): Promise<SignInResult> => {
  try {
    const response = await fetch(SIGN_IN, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      cache: "no-store",
      body: JSON.stringify({ email: request.email, password: request.password, next: request.next ?? null, locale: request.locale ?? null }),
    });
    const body = (await response.json().catch(() => null)) as { ok?: unknown; location?: unknown; code?: unknown } | null;
    if (response.ok && body?.ok === true && typeof body.location === "string" && body.location.startsWith("/")) return { ok: true, location: body.location };
    if (response.status === 401 && body?.code === "invalid_credentials") return { ok: false, code: "invalid_credentials" };
    return { ok: false, code: "unavailable" };
  } catch {
    return { ok: false, code: "unavailable" };
  }
};

/** Whether this browser holds a staff session; any failure counts as signed out. */
export async function getSessionSummary(): Promise<SessionSummary> {
  try {
    const response = await fetch(SESSION, { credentials: "same-origin", cache: "no-store", headers: { Accept: "application/json" } });
    if (!response.ok) return { signedIn: false };
    const body = (await response.json()) as Partial<{ signedIn: boolean; displayName: string; email: string }>;
    return body.signedIn === true && typeof body.displayName === "string" && typeof body.email === "string"
      ? { signedIn: true, displayName: body.displayName, email: body.email }
      : { signedIn: false };
  } catch {
    return { signedIn: false };
  }
}

const SETUP = "/api/access/setup";
const PROBLEMS = ["too_short", "too_long", "common", "repetitive", "sequence", "personal"] as const;

async function postSetup(body: Record<string, unknown>): Promise<{ status: number; body: Record<string, unknown> | null }> {
  const response = await fetch(SETUP, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "same-origin",
    cache: "no-store",
    referrerPolicy: "no-referrer",
    body: JSON.stringify(body),
  });
  return { status: response.status, body: (await response.json().catch(() => null)) as Record<string, unknown> | null };
}

function setupFailure(status: number): { ok: false; code: "invalid_link" | "rate_limited" | "unavailable" } {
  if (status === 410) return { ok: false, code: "invalid_link" };
  if (status === 429) return { ok: false, code: "rate_limited" };
  return { ok: false, code: "unavailable" };
}

/** Whether the setup link still works; the token goes only in this POST's body. */
export async function checkSetupLink(token: string): Promise<SetupCheckResult> {
  try {
    const { status, body } = await postSetup({ token });
    if (status === 200 && body?.ok === true && typeof body.email === "string") return { ok: true, email: body.email };
    return setupFailure(status);
  } catch {
    return { ok: false, code: "unavailable" };
  }
}

/** Sets the password through the link; on success the browser holds a session and gets the cabinet address. */
export async function completeSetup(token: string, password: string, locale: string): Promise<SetupCompleteResult> {
  try {
    const { status, body } = await postSetup({ token, password, locale });
    if (status === 200 && body?.ok === true && typeof body.location === "string" && body.location.startsWith("/")) return { ok: true, location: body.location };
    if (status === 422 && body?.code === "weak_password" && (PROBLEMS as readonly unknown[]).includes(body.problem)) {
      return { ok: false, code: "weak_password", problem: body.problem as (typeof PROBLEMS)[number] };
    }
    return setupFailure(status);
  } catch {
    return { ok: false, code: "unavailable" };
  }
}
